/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { readStoreRequestFailure } from './storerequestfailure';

/** Why an existing order could not be opened: a store that did not answer, or one that would not open it. */
export type UnopenedOrderReason = 'temporary' | 'refused';

export interface IUnopenedOrder {
    storeOrderId: string;
    reason: UnopenedOrderReason;
}

const UNOPENED_ORDER_STORAGE_KEY = 'lac_unopenedOrder';

/** Refusals that say nothing about the order itself, only that the store could not be asked just now. */
const TEMPORARY_RESOURCE_IDS = ['401', 'LSH_IntegrationStoreCallFailed', 'Microsoft_Dynamics_Internal_Server_Error'];

const isServerFailureStatus = (resourceId: string): boolean => /^5\d\d$/.test(resourceId);

/**
 * Reads a failed attempt to open an order. Only a refusal the store named is about the order; anything
 * without a name never got an answer from it and may succeed on the next try.
 */
export const classifyUnopenedOrder = (error: unknown): UnopenedOrderReason => {
    const resourceId = readStoreRequestFailure(error).ErrorCode;

    return resourceId && !TEMPORARY_RESOURCE_IDS.includes(resourceId) && !isServerFailureStatus(resourceId) ? 'refused' : 'temporary';
};

/** The suite and event an order stands for, as the menu and the cart both name them. */
export const getUnopenedOrderScopeKey = (storeId: string, eventId: string, tableId: string): string =>
    storeId && eventId && tableId ? `${storeId}|${eventId}|${tableId}` : '';

const readAll = (): Record<string, IUnopenedOrder> => {
    try {
        return JSON.parse(window.sessionStorage.getItem(UNOPENED_ORDER_STORAGE_KEY) || '{}') as Record<string, IUnopenedOrder>;
    } catch (error) {
        console.log('LTS Info', 'unopenedorder: could not read the orders that would not open', error);

        return {};
    }
};

const writeAll = (orders: Record<string, IUnopenedOrder>): void => {
    try {
        window.sessionStorage.setItem(UNOPENED_ORDER_STORAGE_KEY, JSON.stringify(orders));
    } catch (error) {
        console.log('LTS Info', 'unopenedorder: could not record the orders that would not open', error);
    }
};

/** Records that the guest has an order for this suite and event that the site could not open. */
export const recordUnopenedOrder = (scopeKey: string, order: IUnopenedOrder): void => {
    if (!scopeKey) {
        return;
    }

    writeAll({ ...readAll(), [scopeKey]: order });
};

/** The order for this suite and event that the site could not open, or undefined. */
export const readUnopenedOrder = (scopeKey: string): IUnopenedOrder | undefined => (scopeKey ? readAll()[scopeKey] : undefined);

/** Forgets an order the site has since opened, or that the store no longer holds. */
export const clearUnopenedOrder = (scopeKey: string): void => {
    const orders = readAll();
    if (!scopeKey || !orders[scopeKey]) {
        return;
    }

    delete orders[scopeKey];
    writeAll(orders);
};
